class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    // Verifica se está no popup ou no formulário inline do header
    this.container = document.querySelector("[data-search-popup]");
    this.isInlineSearch = this.closest("[data-header-search-inline]") !== null;

    // Para formulário inline, busca elementos dentro do próprio componente
    const searchContainer = this.isInlineSearch ? this : this.container;
    this.domNodes = queryDomNodes(this.selectors, searchContainer);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    
    // Para formulário inline, usa o container de resultados do header
    if (this.isInlineSearch) {
      this.searchPopupSearults = this.querySelector("[data-predictive-search-results]");
    } else {
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    }
    
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    if (form) {
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    if (this.input) {
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    }
    
    if (clear) {
    clear.addEventListener("click", this.onClearSearch.bind(this));
    }
    
    // Só escuta o evento de fechar popup se não for formulário inline
    if (!this.isInlineSearch) {
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    } else {
      // Para formulário inline, fecha resultados ao clicar fora
      document.addEventListener("click", (e) => {
        if (this.isOpen && !this.contains(e.target)) {
          this.close(true);
        }
      });
      
      // Reposicionar resultados ao rolar ou redimensionar
      const repositionResults = () => {
        if (this.isOpen && this.isInlineSearch && this.searchPopupSearults && this.input) {
          const formElement = this.input.closest('.m-search-form--inline');
          if (formElement) {
            const formRect = formElement.getBoundingClientRect();
            const topPosition = formRect.bottom + 4;
            const leftPosition = formRect.left;
            const width = formRect.width;
            
            this.searchPopupSearults.style.top = `${topPosition}px`;
            this.searchPopupSearults.style.left = `${leftPosition}px`;
            this.searchPopupSearults.style.width = `${width}px`;
            this.searchPopupSearults.style.maxWidth = `${width}px`;
            this.searchPopupSearults.style.minWidth = `${width}px`;
          }
        }
      };
      
      window.addEventListener('scroll', repositionResults, true);
      window.addEventListener('resize', repositionResults);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption;

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(`${searchURL}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input.value;

    // Exibir mensagens para todos os tipos de busca
    if (searchQuery && searchMessage) {
      const { resultsTitle } = searchMessage.dataset;
      searchQuery.textContent = query;

      if (results) {
        searchMessage.textContent = resultsTitle;
        if (moreResultIcon) moreResultIcon.classList.add("m:hidden");
      } else {
        searchMessage.textContent = searchMessage.dataset.resultsTitle;
        if (moreResultIcon) moreResultIcon.classList.remove("m:hidden");
      }
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.remove("m:hidden");
    }
    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.remove("m:hidden");
      
      // Para formulário inline, forçar estilos corretos
      if (this.isInlineSearch) {
        this.searchPopupSearults.style.display = 'block';
        this.searchPopupSearults.style.visibility = 'visible';
        this.searchPopupSearults.style.height = 'auto';
        this.searchPopupSearults.style.minHeight = 'auto';
        this.searchPopupSearults.style.overflow = 'visible';
        this.searchPopupSearults.style.overflowY = 'auto';
      }
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    if (submit) {
    submit.style.visibility = show ? "hidden" : "visible";
    }
    if (loading) {
    loading.style.visibility = show ? "visible" : "hidden";
    }
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    if (clear) {
    clear.style.visibility = show ? "visible" : "hidden";
    }
  }

  open() {
    if (this.searchPopupSearults) {
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
      
      // Para formulário inline, posicionar resultados abaixo do campo
      if (this.isInlineSearch) {
        const formElement = this.input.closest('.m-search-form--inline');
        const resultsContainer = this.searchPopupSearults;
        
        // Garantir que os estilos corretos sejam aplicados toda vez
        resultsContainer.style.display = 'block';
        resultsContainer.style.visibility = 'visible';
        resultsContainer.style.height = 'auto';
        resultsContainer.style.minHeight = 'auto';
        resultsContainer.style.maxHeight = 'calc(100vh - 200px)';
        resultsContainer.style.overflow = 'visible';
        resultsContainer.style.overflowY = 'auto';
        
        if (formElement) {
          const formRect = formElement.getBoundingClientRect();
          
          // Calcular posição fixa baseada na posição do formulário completo
          const topPosition = formRect.bottom + 4;
          const leftPosition = formRect.left;
          const width = formRect.width;
          
          // Garantir que está completamente fora do fluxo do documento
          resultsContainer.style.position = 'fixed';
          resultsContainer.style.top = `${topPosition}px`;
          resultsContainer.style.left = `${leftPosition}px`;
          resultsContainer.style.width = `${width}px`;
          resultsContainer.style.maxWidth = `${width}px`;
          resultsContainer.style.minWidth = `${width}px`;
          resultsContainer.style.transform = 'none';
          resultsContainer.style.margin = '0';
          resultsContainer.style.padding = '0';
          resultsContainer.style.boxSizing = 'border-box';
          
          // Remover do fluxo do documento completamente
          resultsContainer.style.display = 'block';
          resultsContainer.style.visibility = 'visible';
        }
        
        // Garantir que o container pai não expanda
        const parentContainer = this.closest('.m-header__search--inline');
        if (parentContainer) {
          parentContainer.style.height = 'auto';
          parentContainer.style.minHeight = '0';
          parentContainer.style.maxHeight = 'none';
          parentContainer.style.overflow = 'visible';
        }
        
        // Garantir que o predictive-search não expanda
        this.style.height = 'auto';
        this.style.minHeight = '0';
        this.style.maxHeight = 'none';
        this.style.overflow = 'visible';
      }
    }
    this.setAttribute("open", true);
    if (this.input) {
    this.input.setAttribute("aria-expanded", true);
    }
    this.isOpen = true;

    if (this.popularSearches) {
      this.popularSearches.classList.add("m:hidden");
    }
    if (this.searchCount) {
    this.searchCount.classList.remove("m:hidden");
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
      this.input.value = "";
      }
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    if (this.predictiveSearchResults) {
    this.predictiveSearchResults.classList.add("m:hidden");
    }
    if (this.searchPopupSearults) {
    this.searchPopupSearults.classList.add("m:hidden");
    }
    if (this.popularSearches) {
      this.popularSearches.classList.remove("m:hidden");
    }
    if (this.searchCount) {
    this.searchCount.classList.add("m:hidden");
    }
  }

  getResultsMaxHeight() {
    // Para formulário inline, usar altura fixa menor
    if (this.isInlineSearch) {
      return 400;
    }
    
    const predictiveSearchElement = this.closest("predictive-search") || document.querySelector("predictive-search");
    if (predictiveSearchElement) {
    this.resultsMaxHeight =
        window.innerHeight - predictiveSearchElement.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
    }
    // Fallback para altura padrão
    return 400;
  }
}

customElements.define("predictive-search", PredictiveSearch);
